import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import AssemblyStage from './exploded/AssemblyStage';
import { isLowEndDevice, VehicleFallbackImage } from './VehicleViewer';
import { explodedAssetFor } from '../config/assets';
import FloatingHUD from './FloatingHUD';
import useTelemetry from '../hooks/useTelemetry';
import DiagnosticPanel from './DiagnosticPanel';
import VoiceAssistant from './VoiceAssistant';
import AdvancedAnalysis from './AdvancedAnalysis';
import DTCAnalysisPanel from './DTCAnalysisPanel';
import SafetyProtocol from './SafetyProtocol';
import ThermalRunawayWarning from './ThermalRunawayWarning';
import ChatInterface from './ChatInterface';
import KPIDashboard from './KPIDashboard';
import VoiceAssistantPanel from './VoiceAssistantPanel';
import VoiceCallInterface from './VoiceCallInterface';
import ACEModelPanel from './ACEModelPanel';
import ExplodedView from './exploded/ExplodedView';
import { locateParts, useAssembly } from './exploded/useAssembly';
import { DRILL_MIN_EXPLODE, planDrill } from './exploded/knowledge/commands';
import { matchFault } from '../data/faultSets';
import AgentLayout from './agent/AgentLayout';
import useAgent from '../hooks/useAgent';
import { AGENT_ENABLED, AGENT_WS_ENDPOINT, getAgentClientId } from './agent/agentConfig';
import { ActionQueue, routeAgentUiAction, highlightFromAction, liveHighlight } from './agent/agentUi';
import { buildFocus } from './agent/agentFocus';
import { agentHighlightColors } from './exploded/overlay';
import { useParts } from './exploded/knowledge/usePartKnowledge';
import PartDossierPanel from './exploded/knowledge/PartDossierPanel';
import { answerRole } from './exploded/diagnosticPalette';
import { talkModeFromAgentState } from '../hooks/voiceTalk';
import { viewportClassName } from './dashboardLayout';
import dummyBatteryData from '../data/dummyBatteryData';
import dummyMotorData from '../data/dummyMotorData';
import dummyVehicleData from '../data/dummyVehicleData';
import dummyAlerts from '../data/dummyAlerts';
import './Dashboard.css';

// MHI Assistant voice service (mhi-voice). Production goes through Caddy + the access gate.
const VOICE_WSS_ENDPOINT = import.meta.env.VITE_VOICE_WSS_ENDPOINT || 'ws://127.0.0.1:8172/ws';
// Voice actions that also move an open workspace (ExplodedView actionQueue).
const WORKSPACE_VOICE_ACTIONS = new Set(['select', 'explode', 'reset', 'deeper', 'back']);
// Agent mode (CONTRACT 13.9). AGENT_ENABLED is false in production builds (no VITE_AGENT_* key), so
// none of the agent wiring below connects, renders or changes the voice session there.
const CAPTION_MS = 6000;
const WORKSPACE_QUEUE_MAX = 20;
// Demo roles offered by the role switcher (mhi-agent config/roles.yaml demo_roles; auth off only).
const DEMO_ROLES = ['technician', 'service_manager', 'planner', 'certifying_engineer', 'fleet_manager', 'admin', 'viewer'];
const roleLabel = (role) => String(role || '').replace(/_/g, ' ');
// The vertical's copy and units for the generic agent primitives (AgentLayout props).
const CASE_RAIL_HINT = 'No cases yet. Start the line replay, or say “MHI, start the line replay”.';
const AGENT_CURRENCY = 'INR';
const assetLabel = (id) => explodedAssetFor(id).label;
const urlAsks = (re) => {
  try { return re.test(window.location.search); } catch { return false; }
};

const Dashboard = () => {
  const [vehicleData, setVehicleData] = useState({
    battery: dummyBatteryData,
    motor: dummyMotorData,
    vehicle: dummyVehicleData,
    alerts: dummyAlerts
  });

  const [selectedComponent, setSelectedComponent] = useState(null);
  const [activePanels, setActivePanels] = useState({
    diagnostics: false,
    dtc: false,
    voice: false,
    alerts: false,
    analysis: false,
    chat: false,
    kpi: false,
    voicePanel: false,
    aceModel: false
  });
  const [showSafetyProtocol, setShowSafetyProtocol] = useState(false);
  const [showTRAWarning] = useState(false); // Hidden by default to reduce clutter
  const [showVoiceCall, setShowVoiceCall] = useState(false);
  const [showExplodedView, setShowExplodedView] = useState(false);
  // Home stage: the assembly shown on open, shared with the exploded workspace.
  const [stageAsset, setStageAsset] = useState('yaris');
  const [stageT, setStageT] = useState(0);
  const [stageSelected, setStageSelected] = useState(null);
  // A unit a voice select/highlight picked on an isolateOnPick asset: soloed on the stage while it
  // stays selected (several diagnostic units are hidden behind others in the default view).
  const [stageSolo, setStageSolo] = useState(null);
  // The agent's view_highlight: {ids, roles, ranks, colors, label, seq, caseId, asset} (agentUi).
  // Painted only while its case is active and its asset is on the stage (liveHighlight).
  const [agentHighlight, setAgentHighlight] = useState(null);
  const [lowEnd] = useState(() => isLowEndDevice());
  // Chat -> 3D link: parts the most recent answer reasoned over.
  const [focusIds, setFocusIds] = useState(null);
  const [focusLabel, setFocusLabel] = useState('');
  const telemetry = useTelemetry(stageAsset, stageSelected, vehicleData);
  // Voice: the AudioContext is created in the click that opens the panel (GUIDELINES 14).
  const [voicePrearm, setVoicePrearm] = useState(null);
  // Voice "show the trace for …": {id, seq} handed to the workspace's trace.
  const [voiceTrace, setVoiceTrace] = useState(null);
  // ui.actions (voice or agent) while the workspace is open: [{action, seq}], applied there in order.
  const [workspaceQueue, setWorkspaceQueue] = useState([]);
  const voiceSeqRef = React.useRef(0);
  const { tree: stageTree, manifest: stageManifest } = useAssembly({ vehicle: explodedAssetFor(stageAsset).id });
  const stageNode = stageSelected && stageTree?.byId?.has(stageSelected) ? stageTree.byId.get(stageSelected) : null;
  const voiceTelemetry = telemetry ? {
    scope: telemetry.barScope,
    title: telemetry.group?.title ?? explodedAssetFor(stageAsset).label,
    location: telemetry.location ?? null,
    alerts: typeof telemetry.alerts === 'number' ? telemetry.alerts : null,
    // dir: which limit a warning crossed ('low' = running cold), so the voice does not call it overheating.
    metrics: (telemetry.group?.metrics ?? telemetry.bar).map(({ label, text, unit, status, dir }) => ({ label, text, unit, status, dir })),
    // The asset's key readings as well as the selected group, so "what's the oil temperature?" with the
    // valve selected is answered from the oil probe, not the valve's readings or an old fault record.
    ...(telemetry.keyReadings ? {
      asset_title: explodedAssetFor(stageAsset).label,
      asset_metrics: telemetry.keyReadings.map(({ label, text, unit, status, dir }) => ({ label, text, unit, status, dir })),
    } : {}),
  } : null;

  /**
   * A chat answer arrived. Map its knowledge-graph reasoning path onto CAD part
   * ids and point the exploded view at them.
   *
   * Deliberately does NOT force the exploded view open — hijacking the screen
   * while someone is reading an answer would be hostile. It arms the focus, and
   * the chat shows a "Show on model" affordance; opening stays the user's call.
   */
  const handleReasoningPath = React.useCallback(async (path, message) => {
    const names = (path || [])
      .map((p) => p?.node_data?.name || p?.name)
      .filter(Boolean);
    if (names.length === 0) return;
    const ids = await locateParts('ace_ev', names);
    if (ids.length === 0) return;
    setFocusIds(ids);
    setFocusLabel(String(message?.question || message?.content || '').slice(0, 90));
  }, []);
  const [is3DModelLoaded, setIs3DModelLoaded] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState(0);

  // Auto-dismiss loading screen after 8s (handles WebGL failure / old GPU)
  useEffect(() => {
    const timeout = setTimeout(() => setIs3DModelLoaded(true), 8000);
    return () => clearTimeout(timeout);
  }, []);

  // Simulate real-time data updates
  useEffect(() => {
    const interval = setInterval(() => {
      setVehicleData(prevData => ({
        ...prevData,
        battery: {
          ...prevData.battery,
          soc: Math.max(0, prevData.battery.soc - 0.01),
          temperature: {
            ...prevData.battery.temperature,
            pack: parseFloat((prevData.battery.temperature.pack + (Math.random() - 0.5) * 0.5).toFixed(1))
          }
        },
        motor: {
          ...prevData.motor,
          temperature: parseFloat((prevData.motor.temperature + (Math.random() - 0.5) * 1).toFixed(1))
        }
      }));
    }, 2000);

    return () => clearInterval(interval);
  }, []);

  // Loading screen follows the real model load (bytes, then preparing parts).
  const handleStageLoad = React.useCallback(({ phase, pct }) => {
    if (typeof pct === 'number') setLoadingProgress(pct);
    if (phase === 'ready' || phase === 'error') setIs3DModelLoaded(true);
  }, []);

  const openWorkspace = React.useCallback(() => setShowExplodedView(true), []);

  const openVoice = React.useCallback(() => {
    let ctx = null;
    try {
      ctx = new (window.AudioContext || window.webkitAudioContext)();
      ctx.resume().catch(() => {});
    } catch {
      ctx = null;
    }
    setVoicePrearm(ctx ? { ctx } : null);
    setShowVoiceCall(true);
  }, []);

  const closeVoice = React.useCallback(() => {
    setShowVoiceCall(false);
    setVoicePrearm(null);
  }, []);

  /** Voice or agent ui.action -> the same stage state clicks change. */
  const applyUiAction = React.useCallback((action) => {
    const ids = (action.part_ids || []).filter((id) => typeof id === 'string');
    const known = (id) => !stageTree?.byId?.size || stageTree.byId.has(id);
    const atLeast = (min) => (cur) => Math.max(cur, typeof min === 'number' ? min : DRILL_MIN_EXPLODE);
    // The stage is hidden behind an open workspace: move the workspace as well.
    if (showExplodedView && WORKSPACE_VOICE_ACTIONS.has(action.type)
      && !(action.type === 'back' && action.target === 'workspace')) {
      voiceSeqRef.current += 1;
      const item = { action, seq: voiceSeqRef.current };
      setWorkspaceQueue((q) => [...q, item].slice(-WORKSPACE_QUEUE_MAX));
    }
    const solo = (id) => setStageSolo(explodedAssetFor(stageAsset).isolateOnPick
      && stageTree?.byId?.get(id)?.level === 'unit' ? id : null);
    switch (action.type) {
      case 'select': {
        const id = ids.find(known);
        if (id) { setStageSelected(id); setStageT(atLeast(action.t)); solo(id); }
        break;
      }
      case 'highlight': {
        const roles = action.roles || {};
        const id = ids.find((p) => roles[p] === 'cause' && known(p)) ?? ids.find(known);
        if (id) { setStageSelected(id); setStageT(atLeast(DRILL_MIN_EXPLODE)); solo(id); }
        // The workspace selects the first focus id: lead with the same part.
        if (ids.length) { setFocusIds(id ? [id, ...ids.filter((p) => p !== id)] : ids); setFocusLabel('Voice answer'); }
        break;
      }
      case 'explode':
        if (typeof action.t === 'number') setStageT(Math.min(1, Math.max(0, action.t)));
        break;
      case 'reset':
        setStageT(0);
        setStageSelected(null);
        setStageSolo(null);
        // "Reset the view" also drops the agent's painted suspects.
        setAgentHighlight(null);
        break;
      case 'deeper':
      case 'back': {
        if (action.type === 'back' && action.target === 'workspace') {
          setShowExplodedView(false); setVoiceTrace(null); setWorkspaceQueue([]); break;
        }
        const plan = planDrill({ type: action.type === 'back' ? 'up' : 'deeper' },
          { tree: stageTree, selectedId: stageSelected, t: stageT });
        if (plan.select !== undefined) setStageSelected(plan.select);
        if (typeof plan.t === 'number') setStageT(plan.t);
        break;
      }
      case 'open_workspace':
        openWorkspace();
        break;
      case 'trace': {
        // The server fills fault_id when it can; otherwise match the words said against the fault list.
        const faultId = action.fault_id ?? (action.query ? matchFault(stageAsset, action.query) : null);
        if (faultId) setVoiceTrace({ id: faultId, seq: Date.now() });
        openWorkspace();
        break;
      }
      case 'asset':
        // Agent path only (voice never sends it): the asset switch itself happens in onAgentUiAction.
        if (action.asset) { setStageAsset(action.asset); setStageT(0); setStageSelected(null); setStageSolo(null); }
        break;
      default:
        break;
    }
  }, [stageTree, stageSelected, stageT, stageAsset, openWorkspace, showExplodedView]);
  const handleVoiceAction = applyUiAction;

  const closeWorkspace = React.useCallback((state) => {
    setShowExplodedView(false);
    setVoiceTrace(null);
    setWorkspaceQueue([]);
    if (state?.vehicle) {
      setStageAsset(state.vehicle);
      setStageT(state.t ?? 0);
      setStageSelected(state.selectedId ?? null);
    }
  }, []);

  /** The open workspace's selection is the voice focus (same as closing it would set). */
  const syncFromWorkspace = React.useCallback((state) => {
    if (!state?.vehicle) return;
    setStageAsset(state.vehicle);
    setStageT(state.t ?? 0);
    setStageSelected(state.selectedId ?? null);
  }, []);

  const handoff = React.useMemo(
    () => ({ vehicle: stageAsset, t: stageT, selectedId: stageSelected }),
    [stageAsset, stageT, stageSelected],
  );

  /* ---- agent mode (CONTRACT 10.5, 13.9) -------------------------------- */
  const clientId = React.useMemo(() => (AGENT_ENABLED ? getAgentClientId() : null), []);
  const [agentMode, setAgentMode] = useState(() => AGENT_ENABLED && urlAsks(/[?&](agent|case)=/));
  // Hands-free start: ?agent=1 opens the voice panel without a click (after login; App renders the
  // dashboard only once signed in). A browser that keeps audio suspended gets one "Start voice" button.
  const [autoVoice] = useState(() => AGENT_ENABLED && urlAsks(/[?&]agent=1(&|$)/));
  // Talk mode in agent mode: 'wake' by default, then whatever voice reports ("MHI, push to talk").
  const [talkMode, setTalkMode] = useState('wake');
  // Caption of the last agent ui.action, for the normal view (agent mode shows AgentLayout's StageCaption).
  const [agentCaption, setAgentCaption] = useState(null);
  // Voice agent.state (narration, talk mode, the voice confirmation in progress).
  const [voiceAgentState, setVoiceAgentState] = useState(null);
  // What "this" means beyond the stage: draft / approval / panel / output in view (AgentLayout patches).
  const [agentFocusExtra, setAgentFocusExtra] = useState({ draft: null, approvalId: null, panel: null, outputId: null });
  const agentQueueRef = React.useRef(null);
  if (agentQueueRef.current === null) agentQueueRef.current = new ActionQueue({ mapAsset: (a) => explodedAssetFor(a).id });
  // The useAgent api, for its live currentCaseId() (set after the hook below).
  const agentApiRef = React.useRef(null);

  const switchStageAsset = React.useCallback((asset) => {
    setStageAsset(asset);
    setStageT(0);
    setStageSelected(null);
    setStageSolo(null);
    setAgentHighlight(null);
  }, []);

  /**
   * Agent ui.action: only the active case moves the stage; another asset waits for its manifest.
   * Routed against the hook's live active case: a case opened from the rail or a toast re-applies
   * its last ui.action in the same tick, before this component has re-rendered.
   */
  const onAgentUiAction = (action, meta = {}) => {
    const activeCaseId = agentApiRef.current?.currentCaseId?.() ?? null;
    const route = routeAgentUiAction({ action, ...meta }, { activeCaseId, stageAsset });
    if (!route.apply) return;
    if (meta.caption) setAgentCaption({ text: meta.caption, seq: meta.seq ?? Date.now() });
    // switchStageAsset clears the previous highlight, so the new one is set after it.
    if (route.switchAsset) switchStageAsset(route.switchAsset);
    if (action.type === 'highlight') {
      setAgentHighlight(highlightFromAction(action, { ...meta, seq: meta.seq ?? Date.now() },
        { activeCaseId, asset: route.switchAsset ?? stageAsset }));
    }
    if (route.switchAsset) {
      if (action.type !== 'asset') agentQueueRef.current.push({ action, asset: route.switchAsset, seq: meta.seq });
      return;
    }
    if (action.type !== 'asset') applyUiAction(action);
  };

  const onCaseFocus = (caseObj) => {
    const asset = caseObj?.asset;
    if (asset && asset !== stageAsset) switchStageAsset(asset);
  };

  const agent = useAgent({
    url: AGENT_WS_ENDPOINT, enabled: AGENT_ENABLED, clientId, demoRole: null,
    onUiAction: onAgentUiAction, onCaseFocus,
    // A fresh load in agent mode opens the most recent open case (never over ?case= or a user choice).
    autoOpen: agentMode,
  });
  useEffect(() => { agentApiRef.current = agent; }, [agent]);
  const shownHighlight = liveHighlight(agentHighlight, { activeCaseId: agent.activeCaseId, stageAsset });

  // Actions held for an asset switch run once that asset's manifest is the one on the stage.
  const applyUiActionRef = React.useRef(applyUiAction);
  useEffect(() => { applyUiActionRef.current = applyUiAction; });
  useEffect(() => {
    const ready = agentQueueRef.current.takeReady(stageManifest?.vehicle);
    ready.forEach((item) => applyUiActionRef.current(item.action));
  }, [stageManifest]);

  // The caption stays for CAPTION_MS.
  useEffect(() => {
    if (!agentCaption) return undefined;
    const timer = setTimeout(() => setAgentCaption(null), CAPTION_MS);
    return () => clearTimeout(timer);
  }, [agentCaption]);

  const agentOverlayColors = React.useMemo(
    () => (agentMode && shownHighlight ? agentHighlightColors(stageTree, shownHighlight) : null),
    [agentMode, shownHighlight, stageTree],
  );
  const stageParts = useParts(stageAsset, {
    enabled: AGENT_ENABLED && agentMode && explodedAssetFor(stageAsset).knowledge === 'part', tree: stageTree,
  });
  // The part dossier panel AgentLayout shows for wb.panel 'dossier' (MHI-specific, so supplied here).
  const stageDomain = explodedAssetFor(stageAsset).domain ?? null;
  const renderDossier = React.useCallback(({ partId, onSelect, onClose }) => (
    stageParts && stageTree ? (
      <PartDossierPanel nodeId={partId} tree={stageTree} parts={stageParts} domain={stageDomain} onSelect={onSelect} onClose={onClose} />
    ) : (
      <section className="agent-panel">
        <header className="agent-panel__head">
          <div className="agent-panel__titles">
            <span className="agent-panel__kicker">Part dossier</span>
            <h2 className="agent-panel__title">{partId}</h2>
          </div>
          <button type="button" className="agent-icon" onClick={onClose} aria-label="Close the dossier"><X size={14} aria-hidden="true" /></button>
        </header>
        <div className="agent-panel__body"><p className="agent-muted">No part knowledge is available for this asset.</p></div>
      </section>
    )
  ), [stageParts, stageTree, stageDomain]);

  // Leaving the normal view: the legacy panels close, the stage keeps its state.
  useEffect(() => {
    if (!agentMode) return;
    setActivePanels((prev) => Object.fromEntries(Object.keys(prev).map((k) => [k, false])));
  }, [agentMode]);

  // ?agent=1: open the voice panel at once (CONTRACT 10.5 hands-free start).
  useEffect(() => {
    if (autoVoice) setShowVoiceCall(true);
  }, [autoVoice]);

  const onFocusPatch = React.useCallback((patch) => {
    if (!patch || typeof patch !== 'object') return;
    setAgentFocusExtra((prev) => {
      const next = { ...prev };
      for (const key of ['draft', 'approvalId', 'panel', 'outputId']) {
        if (Object.prototype.hasOwnProperty.call(patch, key)) next[key] = patch[key] ?? null;
      }
      return next;
    });
  }, []);

  const agentWb = agent.state.wb;
  const agentFocus = React.useMemo(() => {
    if (!AGENT_ENABLED) return null;
    const ranks = shownHighlight?.ranks;
    const suspects = shownHighlight
      ? [...shownHighlight.ids].sort((a, b) => (ranks?.[a] ?? 99) - (ranks?.[b] ?? 99))
      : [];
    return buildFocus({
      activeCaseId: agent.activeCaseId, stageAsset, stageSelected, stageNode, stageT,
      overlay: agentMode && shownHighlight ? 'agent' : 'none', wb: agentWb,
      draftSel: agentFocusExtra.draft, approvalId: agentFocusExtra.approvalId,
      panel: agentFocusExtra.panel, outputId: agentFocusExtra.outputId, suspects,
    });
  }, [agent.activeCaseId, stageAsset, stageSelected, stageNode, stageT, agentMode, shownHighlight, agentWb,
    agentFocusExtra]);
  const setAgentFocus = agent.setFocus;
  useEffect(() => {
    if (agentFocus) setAgentFocus(agentFocus);
  }, [agentFocus, setAgentFocus]);

  const onSelectPart = React.useCallback((id) => {
    if (id) applyUiActionRef.current({ type: 'select', part_ids: [id] });
  }, []);
  const onVoiceUiMode = React.useCallback((on) => { if (AGENT_ENABLED) setAgentMode(Boolean(on)); }, []);
  // Voice agent.state: keep it, and follow its talk mode so a voice switch is not sent back as 'wake'.
  const onVoiceAgentState = React.useCallback((s) => {
    setVoiceAgentState(s);
    setTalkMode((cur) => talkModeFromAgentState(cur, s));
  }, []);
  const canEditPolicy = Boolean(agent.state.principal?.roles?.includes('admin'));
  const voiceForAgent = React.useMemo(
    () => ({ state: showVoiceCall ? 'on' : 'idle', confirm: voiceAgentState?.confirm ?? null }),
    [showVoiceCall, voiceAgentState],
  );

  const handlePanelToggle = (panelName) => {
    // The HUD "Agent" button enters or leaves agent mode.
    if (panelName === 'agent') {
      if (AGENT_ENABLED) setAgentMode((on) => !on);
      return;
    }
    // The HUD "Voice" button opens the MHI Assistant voice panel.
    if (panelName === 'voicePanel') {
      if (showVoiceCall) closeVoice();
      else openVoice();
      return;
    }
    setActivePanels(prev => {
      const isCurrentlyActive = prev[panelName];

      // Panel exclusivity rules - SIMPLIFIED
      if (!isCurrentlyActive) {
        const newState = { ...prev };

        // Left zone (diagnostics, dtc) - mutually exclusive
        if (panelName === 'diagnostics' || panelName === 'dtc') {
          newState.diagnostics = panelName === 'diagnostics';
          newState.dtc = panelName === 'dtc';
        }
        // Right zone (alerts, analysis) - mutually exclusive
        else if (panelName === 'alerts' || panelName === 'analysis') {
          newState.alerts = panelName === 'alerts';
          newState.analysis = panelName === 'analysis';
        }

        newState[panelName] = true;
        return newState;
      } else {
        // Just toggle off
        return {
          ...prev,
          [panelName]: false
        };
      }
    });
  };

  const handleClosePanel = (panelName) => {
    setActivePanels(prev => ({
      ...prev,
      [panelName]: false
    }));
  };

  // ESC key closes all panels
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        setActivePanels({
          diagnostics: false,
          dtc: false,
          voice: false,
          alerts: false,
          analysis: false,
          chat: false,
          kpi: false,
          voicePanel: false,
          aceModel: false
        });
      }
    };

    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, []);

  return (
    <div className={`immersive-dashboard${agentMode ? ' agent-mode' : ''}`}>
      {/* Loading Screen */}
      {!is3DModelLoaded && (
        <div className="loading-overlay">
          <div className="loading-content">
            <div className="loading-spinner">
              <div className="spinner-ring"></div>
              <div className="spinner-ring"></div>
              <div className="spinner-ring"></div>
            </div>
            <h2 className="loading-title">Connecting to Varahi MHI</h2>
            <p className="loading-subtitle">Opening the vehicle diagnostics link</p>
            <div className="loading-progress-bar">
              <div className="loading-progress-fill" style={{ width: `${loadingProgress}%` }}></div>
            </div>
            <p className="loading-percentage">{Math.round(loadingProgress)}%</p>
            <button
              className="loading-skip"
              onClick={() => setIs3DModelLoaded(true)}
            >
              Skip 3D / open dashboard
            </button>
          </div>
        </div>
      )}

      {/* PRIMARY: Owner's Manual Assistant - Left 60% of screen - Only when chat is active */}
      {activePanels.chat && (
        <div className="primary-chat-section active">
          <ChatInterface
            vehicleData={vehicleData}
            isOpen={true}
            onClose={() => handleClosePanel('chat')}
            isPrimaryMode={true}
            onReasoningPath={handleReasoningPath}
          />
        </div>
      )}

      {/* SECONDARY/PRIMARY: 3D Vehicle Viewer - Expands to full screen when chat is closed.
          Unmounted while the exploded view is open: that overlay is fully opaque, so this
          Canvas would otherwise keep drawing 1.1M vertices + shadow maps into hidden pixels
          and hold a second WebGL context. Both GLBs stay in the useGLTF cache, so reopening
          is instant. */}
      {(agentMode || !showExplodedView) && (
        <div className={viewportClassName({ agentMode, chatOpen: activePanels.chat })}>
          {agentMode && showExplodedView ? (
            // Agent mode: the workspace opens inside the centre stage (CONTRACT 10.5 step 4).
            <ExplodedView
              embedded
              isVisible
              vehicle={stageAsset}
              handoff={handoff}
              onClose={closeWorkspace}
              focusIds={focusIds}
              focusLabel={focusLabel}
              initialTrace={voiceTrace}
              actionQueue={workspaceQueue}
              highlight={shownHighlight}
              onSelectionChange={syncFromWorkspace}
              onVoiceToggle={showVoiceCall ? closeVoice : openVoice}
              voiceOpen={showVoiceCall}
            />
          ) : lowEnd ? (
            <VehicleFallbackImage />
          ) : (
            <AssemblyStage
              assetId={stageAsset}
              onAssetChange={switchStageAsset}
              t={stageT}
              onTChange={setStageT}
              selectedId={stageSelected}
              onSelect={(id) => { setStageSelected(id); setStageSolo(null); }}
              isolatedId={stageSolo && stageSolo === stageSelected ? stageSolo : null}
              onOpenWorkspace={openWorkspace}
              onLoadState={handleStageLoad}
              partTelemetry={telemetry?.group ?? null}
              compact={activePanels.chat}
              overlayColors={agentOverlayColors}
              caption={agentMode ? null : agentCaption?.text ?? null}
              topOffset={agentMode ? 12 : undefined}
            />
          )}
        </div>
      )}

      {/* Floating HUD Overlays - Only show when model is loaded and chat is NOT open */}
      {is3DModelLoaded && !activePanels.chat && (
        <FloatingHUD
          assetLabel={explodedAssetFor(stageAsset).label}
          telemetry={telemetry}
          onPanelToggle={handlePanelToggle}
          activePanels={{ ...activePanels, voicePanel: showVoiceCall, agent: agentMode }}
          agentEnabled={AGENT_ENABLED}
          agentMode={agentMode}
        />
      )}

      {/* Thermal Runaway Warning - Top Center */}
      {showTRAWarning && (
        <div className="tra-warning-overlay fade-in">
          <ThermalRunawayWarning
            batteryData={vehicleData.battery}
            isVisible={showTRAWarning}
          />
        </div>
      )}

      {/* Floating Diagnostics Panel - Left Side - Hidden when chat is open */}
      {activePanels.diagnostics && !activePanels.chat && (
        <div className="floating-panel-left slide-in-left">
          <div className="panel-wrapper">
            <div className="panel-header">
              <h2>Diagnostics</h2>
              <button className="panel-close-btn" onClick={() => handleClosePanel('diagnostics')}>✕</button>
            </div>
            <div className="panel-content">
              <DiagnosticPanel
                data={vehicleData}
                focusComponent={selectedComponent}
                onComponentFocus={setSelectedComponent}
              />
            </div>
          </div>
        </div>
      )}

      {/* DTC Analysis Panel - Left Side - Hidden when chat is open */}
      {!activePanels.chat && (
        <DTCAnalysisPanel
          isVisible={activePanels.dtc}
          onClose={() => handleClosePanel('dtc')}
        />
      )}

      {/* Floating Voice Assistant - Bottom Center - Hidden when chat is open */}
      {activePanels.voice && !activePanels.chat && (
        <div className="floating-panel-bottom slide-in-bottom">
          <div className="panel-wrapper voice-panel">
            <div className="panel-header">
              <h2>Voice assistant</h2>
              <button className="panel-close-btn" onClick={() => handleClosePanel('voice')}>✕</button>
            </div>
            <div className="panel-content">
              <VoiceAssistant
                vehicleData={vehicleData}
                wssEndpoint={VOICE_WSS_ENDPOINT}
              />
            </div>
          </div>
        </div>
      )}

      {/* Floating Alerts Panel - Right Side - Hidden when chat is open */}
      {activePanels.alerts && !activePanels.chat && (
        <div className="floating-panel-right slide-in-right">
          <div className="panel-wrapper">
            <div className="panel-header">
              <h2>Active alerts ({vehicleData.alerts.length})</h2>
              <button className="panel-close-btn" onClick={() => handleClosePanel('alerts')}>✕</button>
            </div>
            <div className="panel-content">
              <div className="alerts-list">
                {vehicleData.alerts.map(alert => (
                  <div key={alert.id} className={`alert-card alert-${alert.severity}`}>
                    <div className="alert-header">
                      <div className="alert-icon-badge">
                        {alert.severity === 'warning' && '⚠️'}
                        {alert.severity === 'critical' && '🚨'}
                        {alert.severity === 'info' && 'ℹ️'}
                      </div>
                      <div className="alert-code">{alert.id}</div>
                    </div>
                    <div className="alert-body">
                      <h4>{alert.title}</h4>
                      <p>{alert.description}</p>
                      <div className="alert-meta">
                        <span className={`alert-status status-${alert.status}`}>
                          {alert.status}
                        </span>
                        <span className="alert-time">Active</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Advanced Analysis Panel - Full Right Overlay - Hidden when chat is open */}
      {!activePanels.chat && (
        <AdvancedAnalysis
          vehicleData={vehicleData}
          isVisible={activePanels.analysis}
          onClose={() => handleClosePanel('analysis')}
        />
      )}

      {/* KPI Dashboard - Full Screen Overlay - Hidden when chat is open */}
      {!activePanels.chat && (
        <KPIDashboard
          vehicleData={vehicleData}
          isVisible={activePanels.kpi}
          onClose={() => handleClosePanel('kpi')}
        />
      )}

      {/* Voice Assistant Panel - Left Side Slide */}
      <VoiceAssistantPanel
        isOpen={activePanels.voicePanel}
        onClose={() => handleClosePanel('voicePanel')}
      />

      {/* Safety Protocol Modal */}
      <SafetyProtocol
        isVisible={showSafetyProtocol}
        onClose={() => setShowSafetyProtocol(false)}
        onProceed={() => {
          console.log('Safety protocol completed');
          // Proceed with HV operation
        }}
      />

      {/* MHI Assistant voice: opened from the HUD "Voice" button; drives the stage via ui.action */}
      <VoiceCallInterface
        isOpen={showVoiceCall}
        onClose={closeVoice}
        wssEndpoint={VOICE_WSS_ENDPOINT}
        prearmedAudio={voicePrearm}
        asset={stageAsset}
        assetLabel={explodedAssetFor(stageAsset).label}
        focusPartId={stageNode ? stageSelected : null}
        focusPartName={stageNode?.name ?? null}
        telemetry={voiceTelemetry}
        onUiAction={handleVoiceAction}
        mode={AGENT_ENABLED ? (agentMode ? 'agent' : 'assistant') : null}
        clientId={clientId}
        talkMode={AGENT_ENABLED && agentMode ? talkMode : null}
        uiFocus={agentMode ? agentFocus : null}
        onUiMode={onVoiceUiMode}
        onAgentState={onVoiceAgentState}
        autoStart={autoVoice}
      />

      {/* Failure matrix panel - offline RAG over the failure-mode data */}
      <ACEModelPanel
        isVisible={activePanels.aceModel}
        onClose={() => handleClosePanel('aceModel')}
      />

      {/* Chat -> 3D: armed, not forced. Opening the model stays the user's call. */}
      {focusIds && focusIds.length > 0 && !showExplodedView && (
        <button
          type="button"
          className="exview-focus-cta"
          onClick={() => setShowExplodedView(true)}
          title="Highlight the parts the last answer reasoned over"
        >
          <span className="exview-focus-cta__dot" aria-hidden="true" />
          Show {focusIds.length} part{focusIds.length === 1 ? '' : 's'} on model
        </button>
      )}

      <ExplodedView
        isVisible={showExplodedView && !agentMode}
        vehicle={stageAsset}
        handoff={handoff}
        onClose={closeWorkspace}
        focusIds={focusIds}
        focusLabel={focusLabel}
        initialTrace={voiceTrace}
        actionQueue={workspaceQueue}
        onSelectionChange={syncFromWorkspace}
        onVoiceToggle={showVoiceCall ? closeVoice : openVoice}
        voiceOpen={showVoiceCall}
      />

      {/* Agent mode around the stage; when closed it renders only the proactive notification toasts. */}
      {AGENT_ENABLED && (
        <AgentLayout
          agent={agent}
          open={agentMode}
          onExit={() => setAgentMode(false)}
          stageAsset={stageAsset}
          onSelectPart={onSelectPart}
          voice={voiceForAgent}
          assetLabel={assetLabel}
          roleLabel={roleLabel}
          renderDossier={renderDossier}
          caseRailHint={CASE_RAIL_HINT}
          defaultCurrency={AGENT_CURRENCY}
          suspectRole={answerRole}
          onFocusPatch={onFocusPatch}
          onOpenCase={() => setAgentMode(true)}
          demoRoles={DEMO_ROLES}
          canEditPolicy={canEditPolicy}
        />
      )}

    </div>
  );
};

export default Dashboard;
